// src/components/DashboardStats.jsx
"use client";

import { useEffect, useState } from "react";
import {
  fetchStatsSummary,
  fetchStatsTopProducts,
  fetchStatsGeo,
} from "@/utils/api";

// ...rest unchanged...


const formatNumber = (value) =>
  typeof value === "number" ? value.toLocaleString() : "--";

const formatOrdersMarketing = (value) => {
  if (typeof value !== "number" || value <= 0) return "--";
  // round down to nearest 10 or 100 and add +
  if (value < 100) {
    const rounded = Math.floor(value / 10) * 10;
    return `${rounded}+`;
  }
  const rounded = Math.floor(value / 100) * 100;
  return `${formatNumber(rounded)}+`;
};

const DashboardStats = () => {
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [geo, setGeo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [s, top, g] = await Promise.all([
          fetchStatsSummary(), // { ordersCount, firstTransactionAt, lastTransactionAt }
          fetchStatsTopProducts({ limit: 5 }), // [{ productSku, productName, type, ordersCount }]
          fetchStatsGeo(), // [{ country, ordersCount }]
        ]);

        if (!mounted) return;
        setSummary(s);
        setTopProducts(Array.isArray(top) ? top : []);
        setGeo(Array.isArray(g) ? g : []);
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Failed to load stats");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const ordersCount = summary?.ordersCount ?? 0;
  const countriesCount = Array.isArray(geo)
    ? geo.filter((row) => (row?.ordersCount || 0) > 0).length
    : 0;
  const sinceYear = summary?.firstTransactionAt
    ? new Date(summary.firstTransactionAt).getFullYear()
    : null;

  const isEmpty = !loading && !error && ordersCount === 0;

  return (
    <div className="w-full space-y-4 md:space-y-6 text-textPrimary overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-textPrimary">
            Marketplace activity
          </h2>
          <p className="text-xs md:text-sm text-textPrimary/70 mt-1">
            Live stats from real orders on ReloadPi.
          </p>
        </div>
        {loading && (
          <span className="text-xs text-textPrimary">Loading…</span>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/40 text-red-200 text-sm px-4 py-3 rounded-lg">
          We couldn&apos;t load stats right now. Please try again in a
          moment.
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="bg-surface border border-border rounded-xl p-4 text-sm text-textPrimary">
          No transactions yet. Once people start buying vouchers, topups or
          eSIMs, you’ll see live stats here.
        </div>
      )}

      {/* Public-facing KPI cards */}
      {!isEmpty && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <StatCard
            label="Orders processed"
            value={formatOrdersMarketing(ordersCount)}
            helper="Real users buying vouchers & topups"
          />
          <StatCard
            label="Countries reached"
            value={
              countriesCount > 0 ? `${formatNumber(countriesCount)}+` : "--"
            }
            helper="Customers from around the world"
          />
          <StatCard
            label="Active since"
            value={sinceYear || "—"}
            helper="Trusted by the Pi community"
          />
        </div>
      )}

      {/* Bottom section: popular products + top countries */}
      {!isEmpty && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
          <TopProductsCard items={topProducts} loading={loading} />
          <GeoCard items={geo} loading={loading} />
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, helper }) => (
  <div className="bg-surface border border-border rounded-xl p-4 md:p-5 shadow-sm animate-fadeIn flex flex-col justify-between">
    <div>
      <div className="text-xs uppercase tracking-wide text-textPrimary/80 mb-1">
        {label}
      </div>
      <div className="text-xl md:text-2xl font-semibold text-primary">
        {value}
      </div>
    </div>
    {helper && (
      <div className="mt-2 text-[11px] text-textPrimary/70">
        {helper}
      </div>
    )}
  </div>
);

const TopProductsCard = ({ items, loading }) => {
  const list = Array.isArray(items) ? items : [];
  const maxOrders = list.reduce(
    (max, item) => Math.max(max, item.ordersCount || 0),
    0
  );
  const totalOrders = list.reduce(
    (sum, item) => sum + (item.ordersCount || 0),
    0
  );

  return (
    <div className="bg-surface border border-border rounded-xl p-4 md:p-5 shadow-sm animate-fadeIn">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-textPrimary">
          Popular products
        </h3>
        {loading && (
          <div className="h-1 w-14 overflow-hidden rounded-full bg-border">
            <div className="h-full w-1/2 bg-primary animate-progress" />
          </div>
        )}
      </div>

      {(!list || list.length === 0) && !loading && (
        <p className="text-xs text-textPrimary">
          No product sales yet.
        </p>
      )}

      {list && list.length > 0 && (
        <div className="space-y-2">
          {list.map((item, index) => {
            const ratio =
              maxOrders > 0 ? (item.ordersCount || 0) / maxOrders : 0;
            const share =
              totalOrders > 0
                ? ((item.ordersCount || 0) / totalOrders) * 100
                : 0;

            return (
              <div
                key={`${item.type}-${item.productSku}-${index}`}
                className="relative overflow-hidden bg-background/40 rounded-lg px-3 py-2 text-xs"
              >
                {/* Background bar */}
                <div
                  className="absolute inset-y-0 left-0 opacity-20 bg-primary"
                  style={{
                    width: `${ratio * 100}%`,
                  }}
                />
                <div className="relative flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-textPrimary">
                       {item.productName || "Popular product"}
                    </div>
                    <div className="text-[11px] text-textPrimary/80 mt-0.5 flex items-center gap-1">
                      <span className="uppercase tracking-wide px-1.5 py-0.5 rounded-full border border-border/70 text-[10px]">
                        {item.type?.toUpperCase() || "N/A"}
                      </span>
                      <span>
                        • {formatNumber(item.ordersCount)} orders
                      </span>
                      {share > 0 && (
                        <span>· {share.toFixed(1)}%</span>
                      )}
                    </div>
                  </div>
                  <div className="ml-3 text-right text-[11px] text-textPrimary/70">
                    #{index + 1}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const GeoCard = ({ items, loading }) => {
  const list = Array.isArray(items) ? items : [];
  const top = list.slice(0, 5);
  const maxOrders = top.reduce(
    (max, row) => Math.max(max, row.ordersCount || 0),
    0
  );

  return (
    <div className="bg-surface border border-border rounded-xl p-4 md:p-5 shadow-sm animate-fadeIn">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-textPrimary">
          Top countries
        </h3>
        {loading && (
          <div className="h-1 w-14 overflow-hidden rounded-full bg-border">
            <div className="h-full w-1/2 bg-accent animate-progress" />
          </div>
        )}
      </div>

      {(!top || top.length === 0) && !loading && (
        <p className="text-xs text-textPrimary">
          No country data available yet.
        </p>
      )}

      {top && top.length > 0 && (
        <div className="space-y-2">
          {top.map((row, index) => {
            const ratio =
              maxOrders > 0 ? (row.ordersCount || 0) / maxOrders : 0;

            return (
              <div
                key={row.country || `UNKNOWN-${index}`}
                className="relative overflow-hidden bg-background/40 rounded-lg px-3 py-2 text-xs"
              >
                {/* Background bar */}
                <div
                  className="absolute inset-y-0 left-0 opacity-20 bg-accent"
                  style={{
                    width: `${ratio * 100}%`,
                  }}
                />
                <div className="relative flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-textPrimary">
                      {row.country || "Unknown"}
                    </div>
                    <div className="text-[11px] text-textPrimary/80 mt-0.5">
                      {formatNumber(row.ordersCount)} orders
                    </div>
                  </div>
                  <div className="ml-3 text-right text-[11px] text-textPrimary/70">
                    #{index + 1}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardStats;
